import React, { useState, useEffect } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "./CustomCalendar.css";
import BackButton from "../../components/common/BackButton";
import { supabase } from "../../lib/supabaseClient";
import { useParams, useNavigate } from "react-router-dom";

import ScheduleControl from "../../components/schedule/ScheduleControl";
import ScheduleModal from "../../components/schedule/ScheduleModal";

function Schedule() {
  const { id: clubId } = useParams();
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState("shared");
  const [sharedSchedules, setSharedSchedules] = useState([]);
  const [personalSchedules, setPersonalSchedules] = useState([]);

  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState("user");
  const [joinedClubIds, setJoinedClubIds] = useState([]);
  const [leaderClubIds, setLeaderClubIds] = useState([]);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [todoText, setTodoText] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const formatDate = (date) => {
    if (!date) return "";

    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const fetchUserInfo = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      alert("로그인을 해주세요!");
      navigate("/login");
      return null;
    }

    const { data, error } = await supabase
      .from("users")
      .select("id, role")
      .eq("email", user.email)
      .maybeSingle();

    if (error) throw error;

    if (!data) {
      alert("사용자 정보를 찾을 수 없습니다.");
      return null;
    }

    return data;
  };

  const fetchJoinedClubIds = async (publicUserId) => {
    const { data, error } = await supabase
      .from("club_members")
      .select("club_id")
      .eq("user_id", publicUserId);

    if (error) throw error;

    return (data || []).map((item) => item.club_id);
  };

  const fetchLeaderClubIds = async (publicUserId) => {
    const { data, error } = await supabase
      .from("club_members")
      .select("club_id")
      .eq("user_id", publicUserId)
      .eq("role", "leader");

    if (error) throw error;

    return (data || []).map((item) => item.club_id);
  };

  const canManageSchedule = (schedule) => {
    if (currentUserRole === "admin") return true;

    if (schedule.club_id && leaderClubIds.includes(schedule.club_id)) {
      return true;
    }

    return false;
  };

  const fetchSchedules = async (userData, myJoinedClubIds) => {
    if (viewMode === "shared") {
      const { data, error } = await supabase
        .from("schedules")
        .select(`
          *,
          users!created_by(role)
        `)
        .eq("is_public", true)
        .order("created_at", { ascending: true });

      if (error) throw error;

      const allSharedSchedules = data || [];

      const filtered =
        userData.role === "admin"
          ? allSharedSchedules
          : allSharedSchedules.filter(
              (schedule) =>
                schedule.users?.role === "admin" ||
                myJoinedClubIds.includes(schedule.club_id)
            );

      setSharedSchedules(filtered);
    } else {
      const { data, error } = await supabase
        .from("schedules")
        .select("*")
        .eq("is_public", false)
        .is("club_id", null)
        .eq("created_by", userData.id)
        .order("created_at", { ascending: true });

      if (error) throw error;

      setPersonalSchedules(data || []);
    }
  };

  const checkAuthAndFetch = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }

      const userData = await fetchUserInfo();
      if (!userData) return;

      setCurrentUserId(userData.id);
      setCurrentUserRole(userData.role || "user");

      const myJoinedClubIds = await fetchJoinedClubIds(userData.id);
      const myLeaderClubIds = await fetchLeaderClubIds(userData.id);

      setJoinedClubIds(myJoinedClubIds);
      setLeaderClubIds(myLeaderClubIds);

      await fetchSchedules(userData, myJoinedClubIds);
    } catch (err) {
      console.error("일정 데이터를 처리하는 중 오류 발생:", err);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    checkAuthAndFetch();
  }, [clubId, viewMode]);

  useEffect(() => {
    if (isModalOpen) return;

    const interval = setInterval(() => {
      checkAuthAndFetch(true);
    }, 3000);

    return () => clearInterval(interval);
  }, [clubId, viewMode, isModalOpen]);

  const activeSchedules =
    viewMode === "shared" ? sharedSchedules : personalSchedules;

  const currentSchedules = activeSchedules.filter(
    (item) => item.date === formatDate(selectedDate)
  );

  const handleAddSchedule = async () => {
    if (!todoText.trim()) {
      alert("일정을 입력해주세요!");
      return;
    }

    if (!currentUserId) {
      alert("사용자 정보를 불러오지 못했습니다.");
      return;
    }

    try {
      let insertClubId = null;

      if (viewMode === "shared") {
        if (currentUserRole === "admin") {
          insertClubId = null;
        } else if (clubId && leaderClubIds.includes(clubId)) {
          insertClubId = clubId;
        } else if (leaderClubIds.length > 0) {
          insertClubId = leaderClubIds[0];
        } else {
          alert("공유 일정 등록 권한이 없습니다. 관리자 또는 동아리장만 가능합니다.");
          return;
        }
      }

      const newScheduleObj = {
        club_id: viewMode === "shared" ? insertClubId : null,
        date: formatDate(selectedDate),
        text: todoText,
        completed: false,
        created_by: currentUserId,
        is_public: viewMode === "shared",
      };

      const { data, error } = await supabase
        .from("schedules")
        .insert([newScheduleObj])
        .select()
        .single();

      if (error) {
        console.error("Supabase insert error:", error);
        alert(error.message);
        return;
      }

      if (viewMode === "shared") {
        const canSee =
          currentUserRole === "admin" ||
          data.club_id === null ||
          joinedClubIds.includes(data.club_id);

        if (canSee) {
          setSharedSchedules((prev) => [...prev, data]);
        }
      } else {
        setPersonalSchedules((prev) => [...prev, data]);
      }

      setTodoText("");
      setIsModalOpen(false);
    } catch (err) {
      console.error("일정 등록 실패:", err);
      alert(err.message || "일정 등록에 실패했습니다.");
    }
  };

  const handleToggleComplete = async (id, currentStatus) => {
    try {
      const targetSchedule = activeSchedules.find((item) => item.id === id);
      if (!targetSchedule) return;

      if (viewMode === "shared" && !canManageSchedule(targetSchedule)) {
        alert("일정 수정 권한이 없습니다.");
        return;
      }

      const { error } = await supabase
        .from("schedules")
        .update({ completed: !currentStatus })
        .eq("id", id);

      if (error) {
        alert(error.message);
        return;
      }

      const updateList = (prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, completed: !currentStatus } : item
        );

      if (viewMode === "shared") {
        setSharedSchedules(updateList);
      } else {
        setPersonalSchedules(updateList);
      }
    } catch (err) {
      console.error("일정 수정 실패:", err);
      alert(err.message || "일정 수정에 실패했습니다.");
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm("정말 이 일정을 삭제하시겠습니까?")) return;

    try {
      const targetSchedule = activeSchedules.find(
        (item) => item.id === scheduleId
      );

      if (!targetSchedule) return;

      if (viewMode === "shared" && !canManageSchedule(targetSchedule)) {
        alert("일정 삭제 권한이 없습니다.");
        return;
      }

      const { error } = await supabase
        .from("schedules")
        .delete()
        .eq("id", scheduleId);

      if (error) {
        alert(error.message);
        return;
      }

      if (viewMode === "shared") {
        setSharedSchedules((prev) =>
          prev.filter((item) => item.id !== scheduleId)
        );
      } else {
        setPersonalSchedules((prev) =>
          prev.filter((item) => item.id !== scheduleId)
        );
      }
    } catch (err) {
      console.error("일정 삭제 실패:", err);
      alert(err.message || "일정 삭제에 실패했습니다.");
    }
  };

  const handleDeleteSelected = async () => {
    const completedItems = currentSchedules.filter((item) => item.completed);

    if (completedItems.length === 0) {
      alert("선택된 완료 항목이 없습니다.");
      return;
    }

    if (!window.confirm(`선택한 ${completedItems.length}개의 일정을 삭제하시겠습니까?`)) {
      return;
    }

    try {
      if (
        viewMode === "shared" &&
        completedItems.some((item) => !canManageSchedule(item))
      ) {
        alert("삭제 권한이 없는 일정이 포함되어 있습니다.");
        return;
      }

      const idsToDelete = completedItems.map((item) => item.id);

      const { error } = await supabase
        .from("schedules")
        .delete()
        .in("id", idsToDelete);

      if (error) {
        alert(error.message);
        return;
      }

      if (viewMode === "shared") {
        setSharedSchedules((prev) =>
          prev.filter((item) => !idsToDelete.includes(item.id))
        );
      } else {
        setPersonalSchedules((prev) =>
          prev.filter((item) => !idsToDelete.includes(item.id))
        );
      }
    } catch (err) {
      console.error("일정 일괄 삭제 실패:", err);
      alert(err.message || "일정 일괄 삭제에 실패했습니다.");
    }
  };

  const renderTileContent = ({ date, view }) => {
    if (view !== "month") return null;

    const formattedTileDate = formatDate(date);

    const tileSchedules = activeSchedules.filter(
      (item) => item.date === formattedTileDate
    );

    if (tileSchedules.length === 0) return null;

    return (
      <div
        style={{
          marginTop: "4px",
          display: "flex",
          flexDirection: "column",
          gap: "2px",
          overflow: "hidden",
          textAlign: "left",
          width: "100%",
          padding: "0 4px",
          boxSizing: "border-box",
        }}
      >
        {tileSchedules.slice(0, 1).map((item) => (
          <div
            key={item.id}
            style={{
              fontSize: "10px",
              color: item.completed ? "#999" : "#2B2B2B",
              textDecoration: item.completed ? "line-through" : "none",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              backgroundColor: viewMode === "shared" ? "#FFF3E0" : "#F5F5F5",
              padding: "1px 3px",
              borderRadius: "3px",
            }}
          >
            {item.text}
          </div>
        ))}

        {tileSchedules.length > 1 && (
          <div
            style={{
              fontSize: "9px",
              color: "#E65100",
              fontWeight: "bold",
              paddingLeft: "3px",
              marginTop: "1px",
            }}
          >
            외 {tileSchedules.length - 1}개 일정
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
        <BackButton />
        <p>인증 정보 및 일정을 확인하는 중...</p>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "20px",
        width: "95%",
        maxWidth: "1200px",
        margin: "0 auto",
        fontFamily: "sans-serif",
      }}
    >
      <BackButton />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          marginBottom: "20px",
          gap: "10px",
        }}
      >
        <h2 style={{ margin: 0 }}>
          {viewMode === "shared" ? "📢 공유 캘린더" : "나의 개인 캘린더"}
        </h2>

        <button
          onClick={() =>
            setViewMode(viewMode === "shared" ? "personal" : "shared")
          }
          style={{
            padding: "10px 20px",
            backgroundColor: viewMode === "shared" ? "#F4A640" : "transparent",
            color: viewMode === "shared" ? "white" : "#F4A640",
            border: viewMode === "shared" ? "none" : "2px solid #F4A640",
            borderRadius: "20px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "bold",
            boxShadow: "0 2px 5px rgba(0,0,0,0.15)",
            transition: "all 0.2s ease",
          }}
        >
          {viewMode === "shared" ? "개인 일정 보기" : "공유 일정 보기"}
        </button>
      </div>

      <div
        style={{
          display: "flex",
          gap: "40px",
          alignItems: "flex-start",
          width: "100%",
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: "1", minWidth: "320px", maxWidth: "1000px" }}>
          <Calendar
            onChange={setSelectedDate}
            value={selectedDate}
            locale="ko-KR"
            tileContent={renderTileContent}
          />
        </div>

        <ScheduleControl
          viewMode={viewMode}
          selectedDate={formatDate(selectedDate)}
          currentSchedules={currentSchedules}
          onOpenModal={() => setIsModalOpen(true)}
          onDeleteSelected={handleDeleteSelected}
          onToggleComplete={handleToggleComplete}
          onDeleteSchedule={handleDeleteSchedule}
        />
      </div>

      <ScheduleModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setTodoText("");
        }}
        viewMode={viewMode}
        selectedDate={formatDate(selectedDate)}
        todoText={todoText}
        setTodoText={setTodoText}
        onAdd={handleAddSchedule}
      />
    </div>
  );
}

export default Schedule;